/**
 * «واریز تومان» operations (Phase 4, OPEN-31). Three views of the deposit pipeline, kept separate on
 * purpose: the user's REQUEST (intent — never money), the bank's EVIDENCE (authoritative inbound
 * events), and RECONCILIATION CASES (where the conservative matcher refused to auto-credit). A
 * credit happens only by an exact unique match or by a Full Admin resolving a case against real
 * evidence (amount = evidence amount, never typed). Manual evidence entry exists for statement-based
 * operation; it goes through the same matcher. Every action: reason + step-up + audit.
 */
import {
  AppContainer,
  Button,
  DataTable,
  formatAmount,
  Ltr,
  PageHeader,
  PageTabs,
  SelectField,
  StateView,
  TextField,
  AmountInput,
} from '@hopex/ui';
import { useState, type ReactNode } from 'react';
import {
  api,
  dt,
  newIdempotencyKey,
  type HopexAccount,
  type InboundEvent,
  type Paged,
  type ReconCase,
  type TomanDeposit,
} from '../../api.ts';
import { CommandDialog, Facts } from '../../lib/command.tsx';
import {
  CASE_KIND,
  caseReason,
  DEPOSIT_METHOD,
  DEPOSIT_STATUS,
  EVENT_STATUS,
  RESOLUTION,
} from '../../lib/money-labels.ts';
import { Remote, useRemote } from '../../lib/remote.tsx';
import { TechCode } from '../../lib/ui.tsx';
import { Counter, num, Pager, Tone, UserCell } from './shared.tsx';

const amt = (v: string | null) =>
  v === null ? '—' : <Ltr className="hx-num">{formatAmount(v, 'TOMAN')}</Ltr>;

/* ───────── requests (intents) ───────── */

function Requests() {
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [reject, setReject] = useState<TomanDeposit | null>(null);
  // OD-63 (owner 2026-10-15): approve straight from the list after checking the bank account.
  const [approve, setApprove] = useState<TomanDeposit | null>(null);
  const [approveAmount, setApproveAmount] = useState('');
  const [approveKey, setApproveKey] = useState(newIdempotencyKey);
  const q = new URLSearchParams({ page: String(page) });
  if (status) q.set('status', status);
  const r = useRemote<Paged<TomanDeposit>>(`/toman-deposits?${q.toString()}`);
  return (
    <div className="flex flex-col gap-3" data-deposit-requests>
      <div className="max-w-xs">
        <SelectField
          label="وضعیت"
          value={status}
          onChange={(v) => (setStatus(v), setPage(1))}
          options={[
            { value: '', label: 'همه' },
            ...Object.entries(DEPOSIT_STATUS).map(([k, v]) => ({ value: k, label: v.label })),
          ]}
        />
      </div>
      <Remote r={r}>
        {(d) => (
          <>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
              {Object.entries(DEPOSIT_STATUS).map(([k, v]) => (
                <Counter key={k} label={v.label} value={num(d.counts?.[k])} />
              ))}
            </div>
            <DataTable<TomanDeposit>
              label="درخواست‌های واریز"
              rows={d.items}
              density="compact"
              stickyFirst
              columns={[
                {
                  id: 'ref',
                  header: 'کد',
                  isRowHeader: true,
                  cell: (x) => <Ltr className="font-mono text-[12px]">{x.ref}</Ltr>,
                },
                {
                  id: 'user',
                  header: 'کاربر',
                  cell: (x) => (
                    <UserCell
                      userId={x.userId}
                      name={x.displayName}
                      publicId={x.publicId}
                      tab="toman"
                    />
                  ),
                },
                { id: 'm', header: 'روش', cell: (x) => DEPOSIT_METHOD[x.method] ?? x.method },
                {
                  id: 'amount',
                  header: 'مبلغ اعلامی',
                  numeric: true,
                  cell: (x) => (
                    <span className="flex flex-col items-end">
                      {amt(x.declaredAmount)}
                      {x.finalAmount && (
                        <span className="text-[11px] text-fg-subtle">
                          نهایی: {amt(x.finalAmount)}
                        </span>
                      )}
                    </span>
                  ),
                },
                {
                  id: 'route',
                  header: 'مبدأ ← مقصد',
                  cell: (x) => (
                    <span className="flex flex-col text-[12px]">
                      <Ltr className="font-mono">{x.source.masked}</Ltr>
                      <span className="text-fg-subtle">{x.destination.label}</span>
                    </span>
                  ),
                },
                {
                  id: 'st',
                  header: 'وضعیت',
                  cell: (x) => <Tone map={DEPOSIT_STATUS} code={x.status} />,
                },
                { id: 'at', header: 'زمان', cell: (x) => dt(x.createdAt) },
                {
                  id: 'act',
                  header: 'عملیات',
                  cell: (x) =>
                    x.status === 'SUBMITTED' || x.status === 'UNDER_REVIEW' ? (
                      <span className="flex flex-wrap gap-1.5">
                        <Button
                          size="sm"
                          onPress={() => (setApprove(x), setApproveAmount(x.declaredAmount))}
                          data-approve-deposit={x.ref}
                        >
                          تأیید
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onPress={() => setReject(x)}
                          data-reject-deposit={x.ref}
                        >
                          رد با دلیل
                        </Button>
                      </span>
                    ) : null,
                },
              ]}
              empty={<StateView compact kind="empty" title="درخواستی با این فیلتر نیست" />}
            />
            <Pager page={d.page} hasMore={d.hasMore} onPage={setPage} />
          </>
        )}
      </Remote>
      {approve && (
        <CommandDialog
          title="تأیید واریز تومان"
          tone="positive"
          confirmLabel="تأیید و واریز به کیف کاربر"
          reasonRequired={false}
          canSubmit={/^\d+(\.\d)?$/.test(approveAmount) && !/^0+(\.0)?$/.test(approveAmount)}
          run={async (reason) => {
            await api.post(
              `/toman-deposits/${approve.id}/approve`,
              {
                ...(approveAmount !== approve.declaredAmount ? { amount: approveAmount } : {}),
                ...(reason ? { reason } : {}),
                expectedVersion: approve.version,
              },
              { 'idempotency-key': approveKey },
            );
          }}
          onClose={() => setApprove(null)}
          onDone={() => (setApprove(null), setApproveKey(newIdempotencyKey()), r.reload())}
          data-approve-dialog
        >
          <Facts
            rows={[
              [
                'درخواست',
                <Ltr key="r" className="font-mono">
                  {approve.ref}
                </Ltr>,
              ],
              ['کاربر', approve.displayName ?? '—'],
              [
                'مبدأ',
                <Ltr key="s" className="font-mono">
                  {approve.source.masked}
                </Ltr>,
              ],
              ['مقصد', approve.destination.label],
              ['مبلغ اعلامی', amt(approve.declaredAmount)],
            ]}
          />
          <AmountInput
            label="مبلغی که به حساب هوپکس رسیده (تومان)"
            value={approveAmount}
            onChange={setApproveAmount}
            unit="تومان"
            scale={1}
          />
          <p className="m-0 text-[12.5px] leading-6 text-fg-muted">
            پیش از تأیید، رسیدن همین مبلغ از همین مبدأ را در حساب بانکی هوپکس ببینید. همین مبلغ به
            کیف کاربر واریز و به او اطلاع داده می‌شود؛ این کار برگشت‌پذیر نیست.
          </p>
        </CommandDialog>
      )}
      {reject && (
        <CommandDialog
          title="رد درخواست واریز"
          tone="danger"
          confirmLabel="رد درخواست"
          run={async (reason) => {
            await api.post(`/toman-deposits/${reject.id}/reject`, {
              reason,
              expectedVersion: reject.version,
            });
          }}
          onClose={() => setReject(null)}
          onDone={() => (setReject(null), r.reload())}
          data-reject-dialog
        >
          <Facts
            rows={[
              [
                'درخواست',
                <Ltr key="r" className="font-mono">
                  {reject.ref}
                </Ltr>,
              ],
              ['کاربر', reject.displayName ?? '—'],
              ['مبلغ اعلامی', amt(reject.declaredAmount)],
              ['اثر', 'درخواست بسته می‌شود؛ موجودی تغییر نمی‌کند.'],
            ]}
          />
        </CommandDialog>
      )}
    </div>
  );
}

/* ───────── bank evidence ───────── */

function RecordEvidence({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const accounts = useRemote<{ items: HopexAccount[] }>('/hopex-bank-accounts');
  const [dest, setDest] = useState('');
  const [channel, setChannel] = useState('CARD_TO_CARD');
  const [amount, setAmount] = useState('');
  const [settled, setSettled] = useState('SETTLED');
  const [externalId, setExternalId] = useState('');
  const [srcKind, setSrcKind] = useState('');
  const [src, setSrc] = useState('');
  const [trace, setTrace] = useState('');
  const live = (accounts.data?.items ?? []).filter((a) => a.status !== 'INACTIVE');
  const destination = dest || live[0]?.id || '';
  const ok =
    destination !== '' &&
    /^\d+(\.\d)?$/.test(amount) &&
    externalId.trim().length >= 3 &&
    (srcKind === '' || src.trim().length > 0);
  return (
    <CommandDialog
      title="ثبت شاهد بانکی (صورت‌حساب)"
      confirmLabel="ثبت و تطبیق"
      canSubmit={ok}
      run={async (reason) => {
        await api.post('/bank-evidence', {
          externalEventId: externalId.trim(),
          destinationAccountId: destination,
          channel,
          amount,
          settlementState: settled,
          ...(srcKind ? { source: { kind: srcKind, value: src.trim() } } : {}),
          ...(trace.trim() ? { traceRef: trace.trim() } : {}),
          reason,
        });
      }}
      onClose={onClose}
      onDone={onDone}
      data-record-evidence
    >
      <p className="m-0 text-[12.5px] leading-6 text-fg-muted">
        فقط اطلاعات صورت‌حساب رسمی حساب هوپکس را وارد کنید. ثبت شاهد به‌تنهایی واریز نیست: همان
        تطبیق‌دهنده‌ی محافظه‌کار اجرا می‌شود و هر ابهامی به پرونده‌ی تطبیق می‌رود.
      </p>
      <SelectField
        label="حساب هوپکس دریافت‌کننده"
        value={destination}
        onChange={setDest}
        options={live.map((a) => ({ value: a.id, label: `${a.label} — ${a.holderName}` }))}
      />
      <div className="grid gap-2 sm:grid-cols-2">
        <SelectField
          label="روش انتقال"
          value={channel}
          onChange={setChannel}
          options={Object.entries(DEPOSIT_METHOD).map(([k, v]) => ({ value: k, label: v }))}
        />
        <SelectField
          label="وضعیت تسویه"
          value={settled}
          onChange={setSettled}
          options={[
            { value: 'SETTLED', label: 'تسویه‌شده' },
            { value: 'PENDING', label: 'در انتظار تسویه' },
          ]}
        />
      </div>
      <AmountInput
        label="مبلغ دریافتی طبق صورت‌حساب (تومان)"
        value={amount}
        onChange={setAmount}
        unit="تومان"
        scale={1}
      />
      <TextField
        label="شناسه‌ی یکتای تراکنش در صورت‌حساب"
        value={externalId}
        onChange={setExternalId}
        ltr
        description="تکرار همین شناسه هرگز دوباره ثبت نمی‌شود."
      />
      <div className="grid gap-2 sm:grid-cols-2">
        <SelectField
          label="مبدأ گزارش‌شده"
          value={srcKind}
          onChange={setSrcKind}
          options={[
            { value: '', label: 'گزارش نشده' },
            { value: 'CARD', label: 'کارت' },
            { value: 'IBAN', label: 'شبا' },
          ]}
        />
        {srcKind && <TextField label="شماره‌ی مبدأ" value={src} onChange={setSrc} ltr />}
      </div>
      <TextField label="کد رهگیری بانکی (اختیاری)" value={trace} onChange={setTrace} ltr />
    </CommandDialog>
  );
}

function Evidence() {
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [recording, setRecording] = useState(false);
  const [retry, setRetry] = useState<InboundEvent | null>(null);
  const q = new URLSearchParams({ page: String(page) });
  if (status) q.set('status', status);
  const r = useRemote<Paged<InboundEvent>>(`/bank-evidence?${q.toString()}`);
  return (
    <div className="flex flex-col gap-3" data-bank-evidence>
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div className="w-full max-w-xs">
          <SelectField
            label="وضعیت"
            value={status}
            onChange={(v) => (setStatus(v), setPage(1))}
            options={[
              { value: '', label: 'همه' },
              ...Object.entries(EVENT_STATUS).map(([k, v]) => ({ value: k, label: v.label })),
            ]}
          />
        </div>
        <Button size="sm" onPress={() => setRecording(true)} data-open-record-evidence>
          ثبت شاهد بانکی
        </Button>
      </div>
      <Remote r={r}>
        {(d) => (
          <>
            <DataTable<InboundEvent>
              label="شواهد بانکی"
              rows={d.items}
              density="compact"
              stickyFirst
              columns={[
                {
                  id: 'ref',
                  header: 'کد',
                  isRowHeader: true,
                  cell: (e) => (
                    <span className="flex flex-col">
                      <Ltr className="font-mono text-[12px]">{e.ref}</Ltr>
                      <TechCode>
                        {e.provider} · {e.externalEventId}
                      </TechCode>
                    </span>
                  ),
                },
                { id: 'amount', header: 'مبلغ', numeric: true, cell: (e) => amt(e.amount) },
                {
                  id: 'ch',
                  header: 'روش / تسویه',
                  cell: (e) => (
                    <span className="flex flex-col text-[12px]">
                      <span>{DEPOSIT_METHOD[e.channel] ?? e.channel}</span>
                      <span className="text-fg-subtle">
                        {e.settlementState === 'SETTLED' ? 'تسویه‌شده' : 'در انتظار تسویه'}
                      </span>
                    </span>
                  ),
                },
                {
                  id: 'src',
                  header: 'مبدأ ← مقصد',
                  cell: (e) => (
                    <span className="flex flex-col text-[12px]">
                      <Ltr className="font-mono">{e.source?.masked ?? 'گزارش نشده'}</Ltr>
                      <span className="text-fg-subtle">{e.destinationLabel}</span>
                    </span>
                  ),
                },
                {
                  id: 'st',
                  header: 'وضعیت',
                  cell: (e) => (
                    <span className="flex flex-col items-start gap-0.5">
                      <Tone map={EVENT_STATUS} code={e.status} />
                      {e.depositRef && (
                        <Ltr className="font-mono text-[11px] text-fg-subtle">{e.depositRef}</Ltr>
                      )}
                    </span>
                  ),
                },
                { id: 'at', header: 'دریافت', cell: (e) => dt(e.createdAt) },
                {
                  id: 'act',
                  header: 'عملیات',
                  cell: (e) =>
                    e.status === 'UNMATCHED' || e.status === 'NEEDS_REVIEW' ? (
                      <Button size="sm" variant="ghost" onPress={() => setRetry(e)}>
                        تطبیق دوباره
                      </Button>
                    ) : null,
                },
              ]}
              empty={<StateView compact kind="empty" title="شاهد بانکی‌ای ثبت نشده است" />}
            />
            <Pager page={d.page} hasMore={d.hasMore} onPage={setPage} />
          </>
        )}
      </Remote>
      {recording && (
        <RecordEvidence
          onClose={() => setRecording(false)}
          onDone={() => (setRecording(false), r.reload())}
        />
      )}
      {retry && (
        <CommandDialog
          title="اجرای دوباره‌ی تطبیق"
          reasonRequired={false}
          confirmLabel="اجرای تطبیق"
          run={async () => {
            await api.post(`/bank-evidence/${retry.id}/retry-match`, {});
          }}
          onClose={() => setRetry(null)}
          onDone={() => (setRetry(null), r.reload())}
        >
          <Facts
            rows={[
              [
                'شاهد',
                <Ltr key="r" className="font-mono">
                  {retry.ref}
                </Ltr>,
              ],
              ['مبلغ', amt(retry.amount)],
              [
                'اثر',
                'همان قواعد تطبیق دقیق دوباره اجرا می‌شود؛ فقط تطبیق یکتا و قطعی واریز می‌کند.',
              ],
            ]}
          />
        </CommandDialog>
      )}
    </div>
  );
}

/* ───────── reconciliation cases ───────── */

function Resolve({
  c,
  onClose,
  onDone,
}: {
  c: ReconCase;
  onClose: () => void;
  onDone: () => void;
}) {
  const candidates = ((c.details?.candidates as string[] | undefined) ?? []).filter(Boolean);
  const pending = useRemote<Paged<TomanDeposit>>('/toman-deposits?status=UNDER_REVIEW');
  const submitted = useRemote<Paged<TomanDeposit>>('/toman-deposits?status=SUBMITTED');
  const known = [...(pending.data?.items ?? []), ...(submitted.data?.items ?? [])];
  const ids = [...new Set([...(c.deposit ? [c.deposit.id] : []), ...candidates])];
  const [action, setAction] = useState<'CREDIT' | 'REJECT_AND_RETURN' | 'DISMISS'>(
    c.deposit ? 'CREDIT' : 'REJECT_AND_RETURN',
  );
  const [depositId, setDepositId] = useState(ids[0] ?? '');
  const [key] = useState(newIdempotencyKey);
  const label = (id: string) => {
    const d = known.find((x) => x.id === id);
    return d
      ? `${d.ref} · ${formatAmount(d.declaredAmount, 'TOMAN')} · ${d.displayName ?? d.publicId}`
      : id;
  };
  return (
    <CommandDialog
      title={`حل پرونده‌ی ${c.ref}`}
      tone={action === 'CREDIT' ? 'positive' : action === 'DISMISS' ? 'caution' : 'danger'}
      confirmLabel={RESOLUTION[action] ?? 'اعمال'}
      canSubmit={action !== 'CREDIT' || depositId !== ''}
      run={async (reason) => {
        await api.post(
          `/reconciliation/cases/${c.id}/resolve`,
          {
            action,
            ...(action === 'CREDIT' ? { depositId } : {}),
            reason,
            expectedVersion: c.version,
          },
          { 'idempotency-key': key },
        );
      }}
      onClose={onClose}
      onDone={onDone}
      data-resolve-case={c.ref}
    >
      <Facts
        rows={[
          ['نوع', CASE_KIND[c.kind] ?? c.kind],
          ['علت', c.reasons.map(caseReason).join('، ')],
          ...(c.event
            ? ([
                [
                  'شاهد بانکی',
                  <span key="e">
                    <Ltr className="font-mono">{c.event.ref}</Ltr> · {amt(c.event.amount)}
                  </span>,
                ],
              ] as [string, ReactNode][])
            : []),
        ]}
      />
      <SelectField
        label="تصمیم"
        value={action}
        onChange={(v) => setAction(v as typeof action)}
        options={(['CREDIT', 'REJECT_AND_RETURN', 'DISMISS'] as const).map((a) => ({
          value: a,
          label: RESOLUTION[a] as string,
        }))}
      />
      {action === 'CREDIT' &&
        (ids.length === 0 ? (
          <p role="alert" className="m-0 text-[13px] text-negative">
            درخواست واریزی برای این شاهد وجود ندارد؛ واریز بدون درخواست ممکن نیست.
          </p>
        ) : (
          <>
            <SelectField
              label="درخواست واریز"
              value={depositId}
              onChange={setDepositId}
              options={ids.map((id) => ({ value: id, label: label(id) }))}
            />
            <p className="m-0 text-[12.5px] leading-6 text-fg-muted">
              مبلغ واریز همان مبلغ شاهد بانکی است (نه عدد واردشده). اگر مبدأ گزارش‌شده متعلق به همین
              درخواست نباشد، سرور واریز را نمی‌پذیرد.
            </p>
          </>
        ))}
      {action === 'REJECT_AND_RETURN' && (
        <p className="m-0 text-[12.5px] leading-6 text-fg-muted">
          وجه به کیف کاربر واریز نمی‌شود و شاهد برای بازگشت وجه به مبدأ علامت می‌خورد.
        </p>
      )}
    </CommandDialog>
  );
}

function Cases() {
  const [status, setStatus] = useState('OPEN');
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<ReconCase | null>(null);
  const q = new URLSearchParams({ page: String(page) });
  if (status) q.set('status', status);
  const r = useRemote<Paged<ReconCase>>(`/reconciliation/cases?${q.toString()}`);
  return (
    <div className="flex flex-col gap-3" data-recon-cases>
      <div className="max-w-xs">
        <SelectField
          label="وضعیت"
          value={status}
          onChange={(v) => (setStatus(v), setPage(1))}
          options={[
            { value: 'OPEN', label: 'باز' },
            { value: 'RESOLVED', label: 'بسته‌شده' },
            { value: '', label: 'همه' },
          ]}
        />
      </div>
      <Remote r={r}>
        {(d) => (
          <>
            <DataTable<ReconCase>
              label="پرونده‌های تطبیق"
              rows={d.items}
              density="compact"
              stickyFirst
              columns={[
                {
                  id: 'ref',
                  header: 'کد',
                  isRowHeader: true,
                  cell: (c) => <Ltr className="font-mono text-[12px]">{c.ref}</Ltr>,
                },
                { id: 'kind', header: 'نوع', cell: (c) => CASE_KIND[c.kind] ?? c.kind },
                {
                  id: 'why',
                  header: 'علت',
                  cell: (c) => (
                    <span className="flex flex-col text-[12.5px]">
                      {c.reasons.map((x) => (
                        <span key={x}>{caseReason(x)}</span>
                      ))}
                    </span>
                  ),
                },
                {
                  id: 'subject',
                  header: 'موضوع',
                  cell: (c) => (
                    <span className="flex flex-col text-[12px]">
                      {c.event && (
                        <span>
                          شاهد <Ltr className="font-mono">{c.event.ref}</Ltr> ·{' '}
                          {amt(c.event.amount)}
                        </span>
                      )}
                      {c.deposit && (
                        <span>
                          واریز <Ltr className="font-mono">{c.deposit.ref}</Ltr>
                        </span>
                      )}
                      {c.withdrawal && (
                        <span>
                          برداشت <Ltr className="font-mono">{c.withdrawal.ref}</Ltr> ·{' '}
                          {amt(c.withdrawal.amount)}
                        </span>
                      )}
                    </span>
                  ),
                },
                {
                  id: 'st',
                  header: 'وضعیت',
                  cell: (c) =>
                    c.status === 'OPEN' ? (
                      'باز'
                    ) : (
                      <span className="flex flex-col text-[12px]">
                        <span>{RESOLUTION[c.resolution ?? ''] ?? 'بسته‌شده'}</span>
                        <span className="text-fg-subtle">
                          <Ltr>{c.resolvedBy ?? '—'}</Ltr> · {dt(c.resolvedAt)}
                        </span>
                      </span>
                    ),
                },
                { id: 'at', header: 'ایجاد', cell: (c) => dt(c.createdAt) },
                {
                  id: 'act',
                  header: 'عملیات',
                  cell: (c) =>
                    c.status === 'OPEN' && c.kind === 'DEPOSIT_EVIDENCE_REVIEW' ? (
                      <Button size="sm" variant="secondary" onPress={() => setOpen(c)}>
                        بررسی و تصمیم
                      </Button>
                    ) : c.status === 'OPEN' && c.withdrawal ? (
                      <span className="text-[12px] text-fg-subtle">از صف برداشت</span>
                    ) : null,
                },
              ]}
              empty={<StateView compact kind="empty" title="پرونده‌ای با این فیلتر نیست" />}
            />
            <Pager page={d.page} hasMore={d.hasMore} onPage={setPage} />
          </>
        )}
      </Remote>
      {open && (
        <Resolve
          c={open}
          onClose={() => setOpen(null)}
          onDone={() => (setOpen(null), r.reload())}
        />
      )}
    </div>
  );
}

export function TomanDepositsPage() {
  const [tab, setTab] = useState('requests');
  return (
    <AppContainer>
      <PageHeader title="واریز تومان" />
      <PageTabs
        label="بخش‌های واریز تومان"
        selected={tab}
        onSelect={setTab}
        items={[
          { id: 'requests', label: 'درخواست‌های کاربران', content: <Requests /> },
          { id: 'evidence', label: 'شواهد بانکی', content: <Evidence /> },
          { id: 'cases', label: 'پرونده‌های تطبیق', content: <Cases /> },
        ]}
      />
    </AppContainer>
  );
}
